import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Download, Trash2 } from 'lucide-react';
import { useAuth, formatApiErrorDetail } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Format a raw dollar-string value like "100000" or "100000.50" as "$100,000" / "$100,000.50"
function formatMoney(v) {
  if (v === undefined || v === null || v === '') return 'N/A';
  const num = Number(v);
  if (Number.isNaN(num)) return String(v);
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
}

export default function SubmissionView() {
  const { submissionId } = useParams();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // Route context wins over role so an admin viewing a submission from the
  // CSA portal returns to the CSA portal (and vice versa). Fall back to role.
  const fromCSA = location.state?.from === 'csa' || user?.role === 'csa';
  const isCSA = user?.role === 'csa';
  const homePath = fromCSA ? '/csa/dashboard' : '/advisor/dashboard';
  const submissionBase = fromCSA ? 'csa' : 'advisor';

  const loadSubmission = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/${submissionBase}/submissions/${submissionId}`, { withCredentials: true });
      setSubmission(data);
    } catch (e) {
      toast.error('Failed to load submission');
      navigate(homePath);
    } finally {
      setLoading(false);
    }
  }, [submissionId, navigate, submissionBase, homePath]);

  useEffect(() => {
    loadSubmission();
  }, [loadSubmission]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    try {
      await axios.delete(`${API}/advisor/submissions/${submissionId}`, { withCredentials: true });
      toast.success('Submission deleted');
      navigate(homePath);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const response = await axios.get(`${API}/advisor/files/${fileId}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success('File downloaded');
    } catch (e) {
      toast.error('Failed to download file');
    }
  };

  const exportToPDF = async () => {
    try {
      toast.info('Generating PDF...');
      const response = await axios.get(
        `${API}/${submissionBase}/submissions/${submissionId}/pdf`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      const safeName = (submission?.client_name || submission?.client_email || 'client').replace(/\s+/g, '_');
      link.href = url;
      link.setAttribute('download', `steelpeak-client-profile-${safeName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF exported successfully');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'PDF export failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5b93d6]"></div>
      </div>
    );
  }

  if (!submission) return null;

  const { data } = submission;

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] shadow-lg">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-y-2">
          <div className="flex items-center gap-4 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(homePath)}
              className="text-white hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Submission Review
            </h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={exportToPDF}
              className="bg-[#5b93d6] hover:bg-[#4a7dc5] text-white"
              data-testid="export-pdf-button"
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            {!isCSA && (
              <Button
                onClick={handleDelete}
                variant="destructive"
                data-testid="delete-submission-button"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8 max-w-5xl">
        {/* Client Info */}
        <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-6 mb-6">
          <h2 className="text-xl font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Client Information
          </h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="min-w-0">
              <span className="text-[#475569]">Name:</span>
              <span className="ml-2 text-[#0f172a] font-medium break-words">{submission.client_name || 'N/A'}</span>
            </div>
            <div className="min-w-0">
              <span className="text-[#475569]">Email:</span>
              <span className="ml-2 text-[#0f172a] font-medium break-all">{submission.client_email}</span>
            </div>
            <div>
              <span className="text-[#475569]">Submitted:</span>
              <span className="ml-2 text-[#0f172a] font-medium">
                {new Date(submission.submitted_at).toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* Contact Details */}
        {data.contact && (
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-6 mb-6">
            <h3 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Contact Details
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {Object.entries(data.contact).map(([key, value]) => (
                <div key={key}>
                  <span className="text-[#475569] capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                  <span className="ml-2 text-[#0f172a]">{value || 'N/A'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Personal Information */}
        {data.personal && ((data.personal.dependents?.length > 0) || data.personal.hasWill || data.personal.bypassTrust || data.personal.dateLastReviewed || data.personal.estateNotes) && (
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-6 mb-6">
            <h3 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Personal Information
            </h3>
            {data.personal.dependents?.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-[#0f172a] mb-2">Dependents</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#f8fafc]">
                      <tr>
                        <th className="px-4 py-2 text-left text-[#475569]">First Name</th>
                        <th className="px-4 py-2 text-left text-[#475569]">Last Name</th>
                        <th className="px-4 py-2 text-left text-[#475569]">Relationship</th>
                        <th className="px-4 py-2 text-left text-[#475569]">Birthdate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#e2e8f0]">
                      {data.personal.dependents.map((dep, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2 text-[#0f172a]">{dep.firstName || 'N/A'}</td>
                          <td className="px-4 py-2 text-[#0f172a]">{dep.lastName || 'N/A'}</td>
                          <td className="px-4 py-2 text-[#0f172a] capitalize">{dep.relationship || 'N/A'}</td>
                          <td className="px-4 py-2 text-[#0f172a]">{dep.birthdate || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {(data.personal.hasWill || data.personal.bypassTrust || data.personal.dateLastReviewed || data.personal.estateNotes) && (
              <div>
                <h4 className="font-medium text-[#0f172a] mb-2">Estate Documents</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {data.personal.hasWill && (
                    <div><span className="text-[#475569]">Has Will:</span> <span className="ml-2 text-[#0f172a] capitalize">{data.personal.hasWill}</span></div>
                  )}
                  {data.personal.bypassTrust && (
                    <div><span className="text-[#475569]">Includes Bypass Trust:</span> <span className="ml-2 text-[#0f172a] capitalize">{data.personal.bypassTrust}</span></div>
                  )}
                  {data.personal.dateLastReviewed && (
                    <div><span className="text-[#475569]">Date Last Reviewed:</span> <span className="ml-2 text-[#0f172a]">{data.personal.dateLastReviewed}</span></div>
                  )}
                  {data.personal.estateNotes && (
                    <div className="col-span-2"><span className="text-[#475569]">Notes:</span> <span className="ml-2 text-[#0f172a]">{data.personal.estateNotes}</span></div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Employment */}
        {data.employment && (
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-6 mb-6">
            <h3 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Employment & Income
            </h3>
            {data.employment.employers?.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-[#0f172a] mb-2">Employers</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#f8fafc]">
                      <tr>
                        <th className="px-4 py-2 text-left text-[#475569]">Employer</th>
                        <th className="px-4 py-2 text-left text-[#475569]">Employment Income</th>
                        <th className="px-4 py-2 text-left text-[#475569]">Total Income</th>
                        <th className="px-4 py-2 text-left text-[#475569]">Liquid Net Worth</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#e2e8f0]">
                      {data.employment.employers.map((emp, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2 text-[#0f172a]">{emp.employer || 'N/A'}</td>
                          <td className="px-4 py-2 text-[#0f172a]">{formatMoney(emp.employmentIncome)}</td>
                          <td className="px-4 py-2 text-[#0f172a]">{formatMoney(emp.totalIncome)}</td>
                          <td className="px-4 py-2 text-[#0f172a]">{formatMoney(emp.liquidNetWorth)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {data.employment.otherIncomes?.length > 0 && (
              <div className="mb-4">
                <h4 className="font-medium text-[#0f172a] mb-2">Other Sources of Income</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#f8fafc]">
                      <tr>
                        <th className="px-4 py-2 text-left text-[#475569]">Type</th>
                        <th className="px-4 py-2 text-left text-[#475569]">Description</th>
                        <th className="px-4 py-2 text-left text-[#475569]">Annual Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#e2e8f0]">
                      {data.employment.otherIncomes.map((o, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2 text-[#0f172a] capitalize">{(o.type || 'N/A').replace(/-/g, ' ')}</td>
                          <td className="px-4 py-2 text-[#0f172a]">{o.description || 'N/A'}</td>
                          <td className="px-4 py-2 text-[#0f172a]">{formatMoney(o.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {data.employment.hasCoClient && data.employment.coClient && (
              <div>
                <h4 className="font-medium text-[#0f172a] mb-2">Co-Client</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(data.employment.coClient).map(([key, value]) => (
                    <div key={key}>
                      <span className="text-[#475569] capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                      <span className="ml-2 text-[#0f172a]">{value || 'N/A'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Goals */}
        {/* Investment Accounts */}
        {data.investments?.accounts?.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-6 mb-6">
            <h3 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Investment Accounts</h3>
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="bg-[#f8fafc]"><tr><th className="px-4 py-2 text-left text-[#475569]">Type</th><th className="px-4 py-2 text-left text-[#475569]">Description</th><th className="px-4 py-2 text-left text-[#475569]">Balance</th></tr></thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {data.investments.accounts.map((a, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-2 text-[#0f172a] capitalize">{(a.type || 'N/A').replace(/-/g, ' ')}</td>
                    <td className="px-4 py-2 text-[#0f172a]">{a.description || 'N/A'}</td>
                    <td className="px-4 py-2 text-[#0f172a]">{formatMoney(a.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        )}

        {/* Other Assets */}
        {data.otherAssets?.assets?.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-6 mb-6">
            <h3 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Other Assets</h3>
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="bg-[#f8fafc]"><tr><th className="px-4 py-2 text-left text-[#475569]">Type</th><th className="px-4 py-2 text-left text-[#475569]">Sub-category</th><th className="px-4 py-2 text-left text-[#475569]">Description</th><th className="px-4 py-2 text-left text-[#475569]">Value</th></tr></thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {data.otherAssets.assets.map((a, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-2 text-[#0f172a] capitalize">{(a.type || 'N/A').replace(/-/g, ' ')}</td>
                    <td className="px-4 py-2 text-[#0f172a] capitalize">{(a.subCategory || '').replace(/-/g, ' ') || '—'}</td>
                    <td className="px-4 py-2 text-[#0f172a]">{a.description || 'N/A'}</td>
                    <td className="px-4 py-2 text-[#0f172a]">{formatMoney(a.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        )}

        {/* Insurance Policy */}
        {data.insurance?.policies?.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-6 mb-6">
            <h3 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Insurance Policy</h3>
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="bg-[#f8fafc]"><tr><th className="px-4 py-2 text-left text-[#475569]">Type</th><th className="px-4 py-2 text-left text-[#475569]">Description</th><th className="px-4 py-2 text-left text-[#475569]">Coverage / Cash Value</th></tr></thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {data.insurance.policies.map((p, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-2 text-[#0f172a] capitalize">{(p.type || 'N/A').replace(/-/g, ' ')}</td>
                    <td className="px-4 py-2 text-[#0f172a]">{p.description || 'N/A'}</td>
                    <td className="px-4 py-2 text-[#0f172a]">{formatMoney(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        )}

        {/* Liabilities */}
        {data.liabilitiesSection?.liabilities?.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-6 mb-6">
            <h3 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Liabilities</h3>
            <div className="overflow-x-auto"><table className="w-full text-sm">
              <thead className="bg-[#f8fafc]"><tr><th className="px-4 py-2 text-left text-[#475569]">Type</th><th className="px-4 py-2 text-left text-[#475569]">Description</th><th className="px-4 py-2 text-left text-[#475569]">Balance Owed</th></tr></thead>
              <tbody className="divide-y divide-[#e2e8f0]">
                {data.liabilitiesSection.liabilities.map((l, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-2 text-[#0f172a] capitalize">{(l.type || 'N/A').replace(/-/g, ' ')}</td>
                    <td className="px-4 py-2 text-[#0f172a]">{l.description || 'N/A'}</td>
                    <td className="px-4 py-2 text-[#0f172a]">{formatMoney(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        )}

        {data.goals && (
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-6 mb-6">
            <h3 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Financial Goals
            </h3>
            <div className="space-y-4 text-sm">
              {data.goals.retirement?.length > 0 && (
                <div>
                  <h4 className="font-medium text-[#0f172a] mb-2">Retirement Goals</h4>
                  {data.goals.retirement.map((goal, idx) => (
                    <div key={idx} className="ml-4 text-[#475569]">
                      Importance: {goal.importance || 'N/A'} · Age to Retire: {goal.ageToRetire || 'N/A'} · Life Expectancy: {goal.lifeExpectancy || 'N/A'}
                    </div>
                  ))}
                </div>
              )}
              {data.goals.college?.length > 0 && (
                <div>
                  <h4 className="font-medium text-[#0f172a] mb-2">College Goals</h4>
                  {data.goals.college.map((goal, idx) => (
                    <div key={idx} className="ml-4 text-[#475569]">
                      {goal.childName}: Start {goal.yearToStart}, {goal.yearsOfCollege} years, Cost: {formatMoney(goal.costEstimate)}
                    </div>
                  ))}
                </div>
              )}
              {data.goals.otherGoals?.length > 0 && (
                <div>
                  <h4 className="font-medium text-[#0f172a] mb-2">Other Financial Goals</h4>
                  {data.goals.otherGoals.map((g, idx) => (
                    <div key={idx} className="ml-4 text-[#475569]">
                      {g.goal}: Year {g.year}, Amount {formatMoney(g.amount)}
                    </div>
                  ))}
                </div>
              )}
              {data.goals.publicSchool?.length > 0 && (
                <div>
                  <h4 className="font-medium text-[#0f172a] mb-2">Public School</h4>
                  {data.goals.publicSchool.map((g, idx) => (
                    <div key={idx} className="ml-4 text-[#475569]">
                      For {g.forDependent}: {g.startYear}-{g.endYear}, Annual {formatMoney(g.annualAmount)}
                    </div>
                  ))}
                </div>
              )}
              {data.goals.privateSchool?.length > 0 && (
                <div>
                  <h4 className="font-medium text-[#0f172a] mb-2">Private School</h4>
                  {data.goals.privateSchool.map((g, idx) => (
                    <div key={idx} className="ml-4 text-[#475569]">
                      For {g.forDependent}: {g.startYear}-{g.endYear}, Annual {formatMoney(g.annualAmount)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Retirement Expenses */}
        {data.retirement && (
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-6 mb-6">
            <h3 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Retirement & Basic Living Expenses
            </h3>
            <div className="space-y-4 text-sm">
              {data.retirement.basicLivingExpenses?.length > 0 && (
                <div>
                  <h4 className="font-medium text-[#0f172a] mb-2">Basic Living Expenses</h4>
                  {data.retirement.basicLivingExpenses.map((exp, idx) => (
                    <div key={idx} className="ml-4 text-[#475569]">
                      {exp.description || 'N/A'}: {formatMoney(exp.amount)} <span className="capitalize">({exp.frequency || 'monthly'})</span>
                    </div>
                  ))}
                </div>
              )}
              {data.retirement.expensesEnding?.length > 0 && (
                <div>
                  <h4 className="font-medium text-[#0f172a] mb-2">Expenses Ending at Retirement</h4>
                  {data.retirement.expensesEnding.map((exp, idx) => (
                    <div key={idx} className="ml-4 text-[#475569]">
                      {exp.description}: End Year {exp.yearEnd}, {formatMoney(exp.amount)}, Inflate: <span className="capitalize">{exp.inflate || 'N/A'}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                {data.retirement.retirementIncome && (
                  <div><span className="text-[#475569]">Retirement Income:</span> <span className="text-[#0f172a]">{formatMoney(data.retirement.retirementIncome)} per {data.retirement.retirementIncomePer || 'year'}</span></div>
                )}
                {data.retirement.inflate && (
                  <div><span className="text-[#475569]">Inflate:</span> <span className="text-[#0f172a] capitalize">{data.retirement.inflate}</span></div>
                )}
                {data.retirement.baseInflationRateToggle && (
                  <div><span className="text-[#475569]">Base Inflation Rate:</span> <span className="text-[#0f172a] capitalize">{data.retirement.baseInflationRateToggle}</span></div>
                )}
                {data.retirement.baseInflationRate && (
                  <div><span className="text-[#475569]">Base Inflation Rate (%):</span> <span className="text-[#0f172a]">{data.retirement.baseInflationRate}%</span></div>
                )}
                {data.retirement.baseInflationPlus && (
                  <div><span className="text-[#475569]">Or base inflation rate +:</span> <span className="text-[#0f172a]">{data.retirement.baseInflationPlus}</span></div>
                )}
                {data.retirement.changeStates === 'yes' && (
                  <>
                    <div><span className="text-[#475569]">Move To:</span> <span className="text-[#0f172a]">{data.retirement.moveState || 'N/A'}</span></div>
                    <div><span className="text-[#475569]">When:</span> <span className="text-[#0f172a]">{data.retirement.whenMove || data.retirement.moveYear || 'N/A'}</span></div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Annual Taxes */}
        {data.taxes && (
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-6 mb-6">
            <h3 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Annual Taxes
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-[#475569]">Federal Income Taxes:</span> <span className="text-[#0f172a]">{formatMoney(data.taxes.federalTaxes)}</span></div>
              <div><span className="text-[#475569]">State Income Taxes:</span> <span className="text-[#0f172a]">{formatMoney(data.taxes.stateTaxes)}</span></div>
              <div><span className="text-[#475569]">FICA:</span> <span className="text-[#0f172a]">{formatMoney(data.taxes.fica)}</span></div>
              <div><span className="text-[#475569]">Real Estate Taxes:</span> <span className="text-[#0f172a]">{formatMoney(data.taxes.realEstateTaxes)}</span></div>
              <div><span className="text-[#475569]">Other Taxes:</span> <span className="text-[#0f172a]">{formatMoney(data.taxes.otherTaxes)}</span></div>
            </div>
          </div>
        )}

        {/* Attachments */}
        {submission.attachments?.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-6">
            <h3 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Attachments ({submission.attachments.length})
            </h3>
            <div className="space-y-2">
              {submission.attachments.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center justify-between p-3 bg-[#f8fafc] rounded-md border border-[#e2e8f0]"
                >
                  <span className="text-sm text-[#0f172a]">{file.original_filename}</span>
                  <Button
                    size="sm"
                    onClick={() => downloadFile(file.id, file.original_filename)}
                    data-testid={`download-file-${file.id}`}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
